import React, { useState } from "react";
import { View, TouchableOpacity } from "react-native";
import { Image } from "@/components/ui/Image";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useI18n } from "@/context/I18nContext";
import type { ApplicationDetail } from "@/lib/api/review";
import { ReadOnlyField } from "./Shared";
import type { MakeUpdateFn, FieldStatus } from "./Shared";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import type { ApplicationStageStatus } from "@/lib/api/vendor";
import { TabBarNavigation } from "@/components/ui/TabBarNavigation";
import { EmptyState } from "@/components/ui/EmptyState";
import { cn } from "@/lib/utils";
import { FilePreview } from "@/components/ui/FilePreview";

// ─── Tab types + config ───────────────────────────────────────────────────────

export type FinancialTab = "bank" | "alipay" | "wechatPay";

interface TabConfig {
  key: FinancialTab;
  labelKey: TranslationKeys;
}

const TABS: TabConfig[] = [
  { key: "bank", labelKey: "vendor.application.detail.tab.bank" },
  { key: "alipay", labelKey: "vendor.application.detail.tab.alipay" },
  { key: "wechatPay", labelKey: "vendor.application.detail.tab.wechatPay" },
];

// ─── FinancialSettlementTabBar ────────────────────────────────────────────────

export interface FinancialSettlementTabBarProps {
  activeTab: FinancialTab;
  onTabPress: (tab: FinancialTab) => void;
  isWide: boolean;
}

export function FinancialSettlementTabBar({
  activeTab,
  onTabPress,
  isWide,
}: FinancialSettlementTabBarProps) {
  const { t } = useI18n();

  const tabs = TABS.map(({ key, labelKey }) => ({ key, label: t(labelKey) }));

  if (isWide) {
    return (
      <TabBarNavigation<FinancialTab>
        tabs={tabs}
        activeTab={activeTab}
        onTabPress={onTabPress}
        containerClassName="py-1 px-10 lg:px-20"
      />
    );
  }

  return (
    <View className="items-start w-full px-5 pt-6 pb-4">
      <View className="w-full flex-row bg-primary-5 border border-primary-15 rounded-lg p-1 items-center">
        {TABS.map(({ key, labelKey }) => (
          <TouchableOpacity
            key={key}
            onPress={() => onTabPress(key)}
            className={
              activeTab === key
                ? "flex-1 py-2 px-3 rounded bg-white items-center justify-center"
                : "flex-1 py-2 px-3 rounded items-center justify-center"
            }
            accessibilityRole="tab"
            accessibilityState={{ selected: activeTab === key }}
          >
            <AppText
              weight={activeTab === key ? "semibold" : "medium"}
              className={
                activeTab === key ? "text-sm text-primary" : "text-sm text-supporting-text-40"
              }
            >
              {t(labelKey)}
            </AppText>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
}

// ─── Bank tab ─────────────────────────────────────────────────────────────────

function BankContent({
  isWide,
  financialSettlement,
}: {
  isWide: boolean;
  financialSettlement?: ApplicationDetail["financialSettlement"];
}) {
  const data = financialSettlement?.bank;
  if (!data)
    return (
      <EmptyState
        title="vendor.application.detail.financialSettlement.empty.title"
        description="vendor.application.detail.financialSettlement.empty.description"
      />
    );

  return (
    <View className={cn("items-start w-full", isWide ? "gap-0" : "px-page-x gap-4")}>
      {/* Account Holder Name — full width */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step4.bank.accountHolderName"
          value={data.accountHolderName}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Account Number */}
      <View className="items-start w-full px-10 py-2">
        <ReadOnlyField labelKey="preVetting.step4.bank.accountNumber" value={data.accountNumber} />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* IBAN + SWIFT — 2-col on desktop */}
      <View className={cn("items-start w-full", isWide ? "flex-row gap-6 px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step4.bank.ibanCode"
          value={data.iban ?? null}
          flex={isWide}
        />
        {!isWide && <View className="border-b border-stroke-50 my-4 w-full" />}
        <ReadOnlyField
          labelKey="preVetting.step4.bank.swiftCode"
          value={data.swiftCode}
          flex={isWide}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Bank Address — full width */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <ReadOnlyField labelKey="preVetting.step4.bank.bankAddress" value={data.bankAddress} />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Settlement Currency — full width */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step4.bank.settlementCurrency"
          value={data.settlementCurrency}
        />
      </View>
    </View>
  );
}

// ─── QR Code modal ────────────────────────────────────────────────────────────

function QrCodeModal({
  visible,
  onDismiss,
  qrCodeUrl,
  titleKey,
}: {
  visible: boolean;
  onDismiss: () => void;
  qrCodeUrl: string;
  titleKey: TranslationKeys;
}) {
  return (
    <Modal visible={visible} onDismiss={onDismiss} title={titleKey}>
      <FilePreview fileKey={qrCodeUrl} />
    </Modal>
  );
}

// ─── AliPay tab ───────────────────────────────────────────────────────────────

function AlipayContent({
  isWide,
  stageStatus,
  onMakeUpdate,
  updatedFields,
  financialSettlement,
  fieldRejections,
}: {
  isWide: boolean;
  stageStatus?: ApplicationStageStatus;
  onMakeUpdate?: MakeUpdateFn;
  updatedFields?: Set<string>;
  financialSettlement?: ApplicationDetail["financialSettlement"];
  fieldRejections?: ApplicationDetail["fieldRejections"];
}) {
  const { t } = useI18n();
  const data = financialSettlement?.alipay;
  const fr = fieldRejections;
  const fs = (key: string): FieldStatus | undefined => {
    if (updatedFields?.has(key)) return "updated";
    return fr?.[key]
      ? stageStatus === "update_required"
        ? "update_required"
        : "rejected"
      : undefined;
  };
  const [qrVisible, setQrVisible] = useState(false);
  if (!data)
    return (
      <EmptyState
        title="vendor.application.detail.financialSettlement.empty.title"
        description="vendor.application.detail.financialSettlement.empty.description"
      />
    );

  return (
    <View className={cn("items-start w-full", isWide ? "gap-0" : "px-page-x gap-4")}>
      {/* Merchant ID — full width */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step4.alipay.merchantId"
          value={data.merchantId}
          fieldStatus={fs("step4.alipay_merchant_id")}
          onMakeUpdate={onMakeUpdate ? () => onMakeUpdate("step4.alipay_merchant_id") : undefined}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Business Name + Email — 2-col on desktop */}
      <View className={cn("items-start w-full", isWide ? "flex-row gap-6 px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step4.alipay.accountHolderName"
          value={data.businessName}
          flex={isWide}
        />
        {!isWide && <View className="border-b border-stroke-50 my-4 w-full" />}
        <ReadOnlyField
          labelKey="preVetting.step4.alipay.accountEmail"
          value={data.email}
          flex={isWide}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Settlement Currency — full width */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step4.alipay.settlementCurrency"
          value={data.settlementCurrency}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* QR Code */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <AppText weight="medium" className="text-base text-supporting-text pt-2">
          {t("preVetting.step4.alipay.qrCode")}
        </AppText>
        <View className="pt-4">
          <Button
            variant="primary"
            label="vendor.application.detail.showQrCode"
            className="self-start"
            onPress={() => setQrVisible(true)}
          />
        </View>
      </View>

      {data.qrCodeUrl && (
        <QrCodeModal
          visible={qrVisible}
          onDismiss={() => setQrVisible(false)}
          qrCodeUrl={data.qrCodeUrl}
          titleKey="preVetting.step4.alipay.qrCode"
        />
      )}
    </View>
  );
}

// ─── WeChat Pay tab ───────────────────────────────────────────────────────────

function WechatPayContent({
  isWide,
  stageStatus,
  onMakeUpdate,
  updatedFields,
  financialSettlement,
  fieldRejections,
}: {
  isWide: boolean;
  stageStatus?: ApplicationStageStatus;
  onMakeUpdate?: MakeUpdateFn;
  updatedFields?: Set<string>;
  financialSettlement?: ApplicationDetail["financialSettlement"];
  fieldRejections?: ApplicationDetail["fieldRejections"];
}) {
  const { t } = useI18n();
  const data = financialSettlement?.wechatPay;
  const fr = fieldRejections;
  const fs = (key: string): FieldStatus | undefined => {
    if (updatedFields?.has(key)) return "updated";
    return fr?.[key]
      ? stageStatus === "update_required"
        ? "update_required"
        : "rejected"
      : undefined;
  };
  const [qrVisible, setQrVisible] = useState(false);
  if (!data)
    return (
      <EmptyState
        title="vendor.application.detail.financialSettlement.empty.title"
        description="vendor.application.detail.financialSettlement.empty.description"
      />
    );

  return (
    <View className={cn("items-start w-full", isWide ? "gap-0" : "px-page-x gap-4")}>
      {/* Merchant ID — full width */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step4.wechatPay.merchantId"
          value={data.merchantId}
          fieldStatus={fs("step4.wechatpay_merchant_id")}
          onMakeUpdate={
            onMakeUpdate ? () => onMakeUpdate("step4.wechatpay_merchant_id") : undefined
          }
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Business Name + Email — 2-col on desktop */}
      <View className={cn("items-start w-full", isWide ? "flex-row gap-6 px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step4.wechatPay.accountHolderName"
          value={data.businessName}
          flex={isWide}
        />
        {!isWide && <View className="border-b border-stroke-50 my-4 w-full" />}
        <ReadOnlyField
          labelKey="preVetting.step4.wechatPay.accountEmail"
          value={data.email}
          flex={isWide}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Account Type + Settlement Currency — 2-col on desktop */}
      <View className={cn("items-start w-full", isWide ? "flex-row gap-6 px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step4.wechatPay.accountType"
          value={data.settlementAccountType}
          flex={isWide}
        />
        {!isWide && <View className="border-b border-stroke-50 my-4 w-full" />}
        <ReadOnlyField
          labelKey="preVetting.step4.wechatPay.settlementCurrency"
          value={data.settlementCurrency}
          flex={isWide}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* QR Code */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <AppText weight="medium" className="text-base text-supporting-text pt-2">
          {t("preVetting.step4.wechatPay.qrCode")}
        </AppText>
        <View className="pt-4">
          <Button
            variant="primary"
            label="vendor.application.detail.showQrCode"
            className="self-start"
            onPress={() => setQrVisible(true)}
          />
        </View>
      </View>

      {data.qrCodeUrl && (
        <QrCodeModal
          visible={qrVisible}
          onDismiss={() => setQrVisible(false)}
          qrCodeUrl={data.qrCodeUrl}
          titleKey="preVetting.step4.wechatPay.qrCode"
        />
      )}
    </View>
  );
}

// ─── FinancialSettlementContent ───────────────────────────────────────────────

export function FinancialSettlementContent({
  isWide,
  activeTab,
  stageStatus,
  onMakeUpdate,
  updatedFields,
  application,
}: {
  isWide: boolean;
  activeTab: FinancialTab;
  stageStatus?: ApplicationStageStatus;
  onMakeUpdate?: MakeUpdateFn;
  updatedFields?: Set<string>;
  application?: ApplicationDetail;
}) {
  return (
    <>
      {activeTab === "bank" && (
        <BankContent isWide={isWide} financialSettlement={application?.financialSettlement} />
      )}
      {activeTab === "alipay" && (
        <AlipayContent
          isWide={isWide}
          stageStatus={stageStatus}
          onMakeUpdate={onMakeUpdate}
          updatedFields={updatedFields}
          financialSettlement={application?.financialSettlement}
          fieldRejections={application?.fieldRejections}
        />
      )}
      {activeTab === "wechatPay" && (
        <WechatPayContent
          isWide={isWide}
          stageStatus={stageStatus}
          onMakeUpdate={onMakeUpdate}
          updatedFields={updatedFields}
          financialSettlement={application?.financialSettlement}
          fieldRejections={application?.fieldRejections}
        />
      )}
    </>
  );
}
